import {Injectable} from '@angular/core';
import {signIn, confirmSignIn, setUpTOTP, verifyTOTPSetup, updateMFAPreference,
    fetchMFAPreference, getCurrentUser, fetchUserAttributes, rememberDevice, forgetDevice, signOut,
    AuthUser, SignInOutput} from '@aws-amplify/auth';
import {readCognitoRefreshCredentials} from './amplify-auth-config';
import {SecretService} from '../secret/secret.service';
import {SecretsInterface} from '../../interfaces/secrets/secrets.interface';
import {AppSettings} from '../../app.settings';
import { HttpClient } from '@angular/common/http';
import { CognitoIdentityProviderClient, InitiateAuthCommand } from '@aws-sdk/client-cognito-identity-provider';
import {MatSnackBar} from "@angular/material/snack-bar";
import { TabStorageService } from '../tabstorage/tabstorage.service';


export interface userCredentialsInterface {
    username: string;
    password: string;
}

@Injectable({
    providedIn: 'root'
})
export class CognitoMfaService {
    currentCognitoUser?: AuthUser;
    private secrets?: SecretsInterface;

    constructor(
        private secretService: SecretService,
        private snackBar: MatSnackBar,
        private http: HttpClient,
        private tabStorageService: TabStorageService,
    ) {
        this.getAppSecrets();
    }

    getAppSecrets() {
        this.secretService.secrets.subscribe({
            next: (secrets: SecretsInterface | null): void => {
                if (secrets) {
                    this.secrets = secrets;
                }
            }
        });
    }

    async cognitoMFASignIn(userCredentials: userCredentialsInterface): Promise<SignInOutput> {
        this.currentCognitoUser = undefined;
        try {
            // V6 rejects signIn while authenticated; start a fresh password flow.
            // Sign-out retains remembered-device metadata for the confirmation-page login.
            await signOut();
            const result = await signIn({...userCredentials, options: {authFlowType: 'USER_SRP_AUTH'}});
            if (result.isSignedIn) await this.cognitoUpdateCurrentUser();
            return result;
        } catch (error) {
            this.snackBar.open('Error while saving cognito information');
            throw error;
        }
    }

    async cognitoMFASignInWithoutPassword(email: string): Promise<SignInOutput | undefined> {
        this.currentCognitoUser = undefined;
        const input = {username: email, options: {authFlowType: 'CUSTOM_WITHOUT_SRP' as const}};
        try {
            await signOut();
            return await signIn(input);
        } catch (error: any) {
            if (error.name === 'UserLambdaValidationException') {
                // Preserve backend verification and Cognito provisioning for first-time users.
                try {
                    await this.http.post(`${AppSettings.getEndpoint()}auth/verify/login-without-pass`, {email}).toPromise();
                    return await signIn(input);
                } catch (httpError: any) {
                    this.snackBar.open(httpError.error?.errors ?? 'Could not authenticate.');
                    return;
                }
            }
            this.snackBar.open(`${error}`);
            return;
        }
    }

    async cognitoCustomChallengeAnswer(code: string): Promise<SignInOutput> {
        return this.completeChallenge(code);
    }

    async cognitoMFAConfirmSignIn(TOTPCode: string): Promise<SignInOutput> {
        return this.completeChallenge(TOTPCode);
    }

    private async completeChallenge(code: string): Promise<SignInOutput> {
        const result = await confirmSignIn({challengeResponse: code});
        if (!result.isSignedIn) throw new Error('Cognito sign-in challenge was not completed.');
        await this.cognitoUpdateCurrentUser();
        return result;
    }

    async cognitoSetupTOTP(): Promise<string> {
        await this.cognitoUpdateCurrentUser();
        return (await setUpTOTP()).sharedSecret;
    }

    async cognitoVerifyTOTPToken(totpCode: string): Promise<void> {
        await this.cognitoUpdateCurrentUser();
        await verifyTOTPSetup({code: totpCode});
    }

    async cognitoSetPreferredMFA(): Promise<void> {
        await this.cognitoUpdateCurrentUser();
        await updateMFAPreference({totp: 'PREFERRED'});
    }

    async cognitoRemoveMFA(): Promise<void> {
        await this.cognitoUpdateCurrentUser();
        await updateMFAPreference({totp: 'DISABLED'});
        await forgetDevice();
    }

    async cognitoUpdateCurrentUser(): Promise<void> {
        this.currentCognitoUser = await getCurrentUser();
    }

    async cognitoRegisterUserDevice(): Promise<void> {
        await rememberDevice();
    }

    async getCurrentCognitoUser(): Promise<string> {
        await this.cognitoUpdateCurrentUser();
        const {preferred} = await fetchMFAPreference();
        // Preserve the profile page's existing MFA status contract.
        if (preferred === 'TOTP') return 'SOFTWARE_TOKEN_MFA';
        if (preferred === 'SMS') return 'SMS_MFA';
        if (preferred === 'EMAIL') return 'EMAIL_OTP';
        return 'NOMFA';
    }

    async cognitoSignOut(): Promise<void> {
        await signOut();
        this.currentCognitoUser = undefined;
    }

    async getAccessTokenUsingRefreshToken(refreshToken: string, deviceKey: string): Promise<{ idToken?: string, accessToken?: string } | null> {
        const REGION =this.secrets?.region; // Your AWS Region
        const CLIENT_ID = this.secrets?.userPoolWebClientId;

        const client = new CognitoIdentityProviderClient({ region: REGION });

        return new Promise<{ idToken?: string, accessToken?: string } | null>(async (resolve) => {
            try {
                const command = new InitiateAuthCommand({
                    AuthFlow: "REFRESH_TOKEN_AUTH",
                    ClientId: CLIENT_ID,
                    AuthParameters: {
                        REFRESH_TOKEN: refreshToken,
                        DEVICE_KEY: deviceKey,
                    },
                });

                const response = await client.send(command);
                resolve({idToken: response.AuthenticationResult?.IdToken, accessToken: response.AuthenticationResult?.AccessToken});
                // The response will contain new ID and access tokens
            } catch (error) {
                resolve(null)
                console.error("Error refreshing session:", error);
            }
        });

    }

    async saveCognitoRefreshTokenInformation(): Promise<void> {
        const user = await getCurrentUser();
        const {email: userEmail} = await fetchUserAttributes();
        if (!userEmail) throw new Error('Cognito user email is missing.');
        const {refreshToken, deviceKey} = await readCognitoRefreshCredentials(user.username);
        const body = {userEmail, refreshToken, deviceKey};
        const jwtToken = this.tabStorageService.getStorage(TabStorageService.JWT_TOKEN);
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`
        };
        try {
            await this.http.post(
                `${AppSettings.getEndpoint()}auth/save-cognito-refresh-token`,
                body,
                {headers}
            ).toPromise();
        } catch (error) {
            console.log(`Error while saving refreshToken: ${error}`);
        }
    }

    generatePassword(): string {
        const minLength = 12;
        const numbers = '0123456789';
        const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
        const specialChars = '!@#$%^&*()-_=+[]{}|;:,.<>?';
        const allChars = numbers + upperCase + lowerCase + specialChars;
        const requiredChars = [
            numbers[Math.floor(Math.random() * numbers.length)],
            upperCase[Math.floor(Math.random() * upperCase.length)],
            lowerCase[Math.floor(Math.random() * lowerCase.length)],
            specialChars[Math.floor(Math.random() * specialChars.length)],
        ];
        const remainingLength = minLength - requiredChars.length;
        for (let i = 0; i < remainingLength; i++) {
            requiredChars.push(allChars[Math.floor(Math.random() * allChars.length)]);
        }
        return requiredChars
            .sort(() => Math.random() - 0.5)
            .join('');
    }
}
